// EXTERNAL
import React, { useEffect, useState } from "react";
// COMPONENTS
import * as ComponentLibrary from "@picnicmedia/masanatemples123";
// FUNCTIONS
import { applicableFormatsForAdSize } from "../functions/applicableFormats";
// CONTEXT
import { useCreativeDispatch, useCreativeState } from "../../../../contexts/creative.context";
// CONFIGS
import studio_config from "../../../../configs/studio.json";
// TYPES
import { StudioConfig } from "../types";
import { Slide } from "../../../../types/contextType";

interface MatchAdSizeToRatio {
  [adSlot: string]: string[];
}

interface AdSizeProps {
  updateAdSize: (adSize: string) => void;
  applicableFormats: (applicableFormat: string[]) => void;
}

export function AdSize({ updateAdSize, applicableFormats }: AdSizeProps) {
  const creative = useCreativeState();
  const creativeDispatch = useCreativeDispatch();
  const studioConfig: StudioConfig = studio_config;
  const [activeButton, setActiveButton] = useState<string | null>(null);
  const activeAdSizes: string[] = studioConfig.activeAdSizes.map((options: MatchAdSizeToRatio) => Object.keys(options)[0]);

  /**
   * JA (18-08-23)
   * Matching ad size to the applicable ratios.
   * Converts studioConfig.activeAdSizes to an object of key-value pairs.
   */
  const matchAdSizeToApplicableFormats = (studioConfig.activeAdSizes as MatchAdSizeToRatio[]).reduce((result, curr) => ({ ...result, ...curr }), {});

  const handleClick = (e: React.MouseEvent<Element, MouseEvent> | undefined, adSize: string) => {
    (e?.currentTarget as HTMLElement).blur();
    updateAdSize(adSize);
    setActiveButton(adSize);
    applicableFormats(applicableFormatsForAdSize(studioConfig, adSize));

    /**
     * JA (18-08-23)
     * Updates aspect ratio with the value that matches the ad size selected.
     */
    let aspectRatio: string;

    switch (adSize) {
      case "MPU (300:250)":
        aspectRatio = "mpu";
        break;
      case "DMPU (300:600)":
        aspectRatio = "dmpu";
        break;
      case "LMPU (336:280)":
        aspectRatio = "lmpu";
        break;
      default:
        aspectRatio = "picnic";
        break;
    }
    /**
     * JA (18-08-23)
     * Anytime a user switches ad size, reset the format to post, per product requirements:
     * https://www.notion.so/picnicmedia/UI-component-for-selecting-new-IAB-sizes-9fbaf5cfd14f49aa8fc9e31999a6f122?pvs=4#17a376d71d3b4e1cb03cf891b9423bdd
     */
    creativeDispatch({
      type: "UPDATE_CREATIVE_METADATA_FORMAT",
      payload: { format: "post" },
    });
    /**
     * JA (21-08-23)
     * Update aspect ratio value to the first applicable value for the format and ad size.
     */
    creativeDispatch({
      type: "UPDATE_CREATIVE_METADATA_ASPECTRATIO",
      payload: { aspectRatio },
    });
    /**
     * JA (21-08-23)
     * Update reactions visibility value to false so the reactions component is not run when format resets to post.
     */
    creativeDispatch({
      type: "UPDATE_CREATIVE_METADATA_FEATURES_REACTIONS_VISIBLE",
      payload: { visible: false },
    });
    /**
     * JA (21-08-23)
     * This payload type is only used for carousels for individual frame clickouts
     * Carousel MPU does not have individual frame clickouts therefore we reset the value to be empty
     */
    if (adSize === "MPU (300:250)") {
      creative.metadata.slides.map((slide: Slide) => {
        creativeDispatch({
          type: "UPDATE_CREATIVE_METADATA_SLIDE_CLICKOUT",
          payload: {
            id: slide.slideNumber,
            clickout: "",
          },
        });
      });
    }
  };

  /**
   * JA (18-08-23)
   * On ad creator load, automatically set the ad size based on the creative.metadata.aspectRatio.
   */
  useEffect(() => {
    for (const key in matchAdSizeToApplicableFormats) {
      if (matchAdSizeToApplicableFormats[key].includes(creative.metadata.aspectRatio)) {
        updateAdSize(key);
        setActiveButton(key);
        applicableFormats(applicableFormatsForAdSize(studioConfig, key));
        break;
      }
    }
  }, [creative.metadata.aspectRatio]);

  return (
    <>
      <p className="text-size-5 color-text-5 platform-font ma0 fw5">{"Ad Size"}</p>
      <ComponentLibrary.PCSpacer type="margin" position="top" size={4} />
      <div className="border-color-neutral-3 ba padding-around-1 border-radius-4 flex flex-wrap" style={{ justifyContent: "space-evenly" }}>
        {activeAdSizes.map((adSize: string, index: number) => (
          <div key={index}>
            <ComponentLibrary.PCActionButton clickHandler={(e) => handleClick(e, adSize)} text={adSize} textColor={activeButton === adSize ? "color-primary-3" : "color-text-2"} color={activeButton === adSize ? "background-color-neutral-2" : "background-color-neutral-1"} padding="padding-left-3 padding-right-3" />
          </div>
        ))}
      </div>
    </>
  );
}
